import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import useFormData from "@/hooks/useFormData";
import { Trash2 } from "lucide-react";
import * as React from "react";
import {
  useGetInstructorVideosQuery,
  useDeleteVideoMutation,
} from "@/store/videoApi";
import { useGetBatchQuery } from "@/store/batchApi";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { getYoutubeId } from "@/lib/utils";

const batchOrder = [
  "HTML",
  "CSS",
  "JavaScript",
  "React",
  "Backend",
  "Basic",
  "Intermediate ",
  "Advanced",
];

const AllUploads = () => {
  const { user } = useSelector((state: any) => state.user);
  const [deleteVideo, { isLoading: isDeleting }] = useDeleteVideoMutation();

  const { formData, handleChange } = useFormData({
    title: "",
    url: "",
    batch: "",
    batchModule: "",
  });

  const { data: batches } = useGetBatchQuery();

  const { data } = useGetInstructorVideosQuery({
    batch: formData.batch,
    batchModule: formData.batchModule,
    Token: user?.token,
  });

  const videosArray = Array.isArray(data)
    ? data
    : data && typeof data === "object" && Array.isArray(data.videos)
      ? data.videos
      : [];

  const handleDelete = async (videoId: string) => {
    try {
      await deleteVideo({ videoId, token: user?.token }).unwrap();
      toast.success("Video deleted successfully!");
    } catch (error) {
      toast.error("Failed to delete video.");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h1 className="text-2xl font-bold">Uploaded Content</h1>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="space-y-2 flex-1">
            <select
              id="batch"
              name="batch"
              className="border rounded-md px-3 py-2 w-full cursor-pointer bg-background"
              onChange={handleChange}
              value={formData.batch}
            >
              <option value="">Select Batch</option>
              {batches?.map((batch: any, index: number) => (
                <option key={index} value={batch._id}>
                  {batch?.bCode}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2 flex-1">
            <select
              id="batchModule"
              name="batchModule"
              className="border rounded-md px-3 py-2 w-full cursor-pointer bg-background"
              onChange={handleChange}
              value={formData.batchModule}
            >
              <option value="">Select Module</option>
              {batchOrder.map((module, index) => (
                <option key={index} value={module.trim()}>
                  {module}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Video Grid */}
        <div className="sm:p-4 p-1">
          {videosArray.length === 0 ? (
            <p className="text-center text-muted-foreground">
              No videos available for the selected filters.
            </p>
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {videosArray.map((video: any) => {
                const videoId = getYoutubeId(video?.url);
                const uploadDate = new Date(video?.createdAt);
                const formattedDate = uploadDate.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                });

                return (
                  <li
                    key={video?._id}
                    className="p-4 border rounded-lg shadow-md bg-white dark:bg-gray-800 flex flex-col gap-4 relative transition-shadow hover:shadow-lg"
                  >
                    {/* Module Badge */}
                    {video?.batchModule && (
                      <span className="absolute top-2 right-2 bg-green-500 text-white text-xs font-semibold py-1 px-3 rounded-full">
                        {video?.batchModule.trim()}
                      </span>
                    )}

                    {/* YouTube Embed */}
                    <div className="aspect-video rounded-lg overflow-hidden">
                      <iframe
                        className="w-full h-full"
                        src={`https://www.youtube.com/embed/${videoId}`}
                        title={video?.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>

                    {/* Title, Date & Delete Button */}
                    <div className="flex items-start justify-between">
                      <div className="flex flex-col">
                        <h3 className="font-semibold text-lg text-gray-800 dark:text-gray-100 line-clamp-2">
                          {video?.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                          Uploaded on: {formattedDate}
                        </p>
                      </div>

                      {/* Delete Confirmation Dialog */}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button className="text-red-600 hover:text-red-800 transition-colors">
                            <Trash2 size={22} />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Video?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to permanently delete "
                              <span className="font-medium">{video?.title}</span>
                              "? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(video._id)}
                              disabled={isDeleting}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              {isDeleting ? "Deleting..." : "Delete"}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AllUploads;

